/**
 * Lead Scoring API - Auto-score leads based on behavior
 */
import apiClient from './index'

export interface LeadScoringRule {
  id: string
  name: string
  description?: string
  rule_type: 'attribute' | 'behavior'
  attribute_field?: string
  attribute_operator?: string
  attribute_value?: string
  behavior_type?: string
  behavior_count: number
  behavior_timeframe_days: number
  score_adjustment: number
  priority: number
  is_active: boolean
  created_at: string
  updated_at?: string
}

export interface LeadScore {
  id: string
  contact_id: string
  contact?: {
    name: string
    email: string
    company?: string
  }
  attribute_score: number
  behavior_score: number
  total_score: number
  grade: string
  applied_rules?: Array<{
    rule_id: string
    rule_name: string
    type: string
    score: number
    count?: number
  }>
  score_history?: Array<{
    old_score: number
    new_score: number
    change: number
    changed_at: string
  }>
  last_calculated_at?: string
  needs_calculation?: boolean
}

export interface LeadScoringConfig {
  decay_enabled: boolean
  decay_percent_per_month: number
  grade_a_threshold: number
  grade_b_threshold: number
  grade_c_threshold: number
  grade_d_threshold: number
  max_attribute_score: number
  max_behavior_score: number
}

export interface ScoringOption {
  value: string
  label: string
}

export interface ScoringOptions {
  rule_types: ScoringOption[]
  attribute_fields: ScoringOption[]
  attribute_operators: ScoringOption[]
  behavior_types: ScoringOption[]
}

export interface CreateRuleData {
  name: string
  description?: string
  rule_type: 'attribute' | 'behavior'
  attribute_field?: string
  attribute_operator?: string
  attribute_value?: string
  behavior_type?: string
  behavior_count?: number
  behavior_timeframe_days?: number
  score_adjustment: number
  priority?: number
  is_active?: boolean
}

export interface RuleResponse {
  data: LeadScoringRule
}

export interface RulesResponse {
  data: LeadScoringRule[]
}

export interface ScoreResponse {
  data: LeadScore
}

export interface ScoresResponse {
  data: LeadScore[]
}

export interface ConfigResponse {
  data: LeadScoringConfig
}

export interface OptionsResponse {
  data: ScoringOptions
}

// ============== Rules ==============

/**
 * Get all scoring rules
 */
export async function getScoringRules(): Promise<LeadScoringRule[]> {
  const response = await apiClient.get<RulesResponse>('/lead-scoring/rules')
  return response.data.data
}

/**
 * Get a scoring rule by ID
 */
export async function getScoringRule(ruleId: string): Promise<LeadScoringRule> {
  const response = await apiClient.get<RuleResponse>(`/lead-scoring/rules/${ruleId}`)
  return response.data.data
}

/**
 * Create a new scoring rule
 */
export async function createScoringRule(data: CreateRuleData): Promise<LeadScoringRule> {
  const response = await apiClient.post<RuleResponse>('/lead-scoring/rules', data)
  return response.data.data
}

/**
 * Update a scoring rule
 */
export async function updateScoringRule(
  ruleId: string,
  data: Partial<CreateRuleData>
): Promise<LeadScoringRule> {
  const response = await apiClient.put<RuleResponse>(`/lead-scoring/rules/${ruleId}`, data)
  return response.data.data
}

/**
 * Delete a scoring rule
 */
export async function deleteScoringRule(ruleId: string): Promise<void> {
  await apiClient.delete(`/lead-scoring/rules/${ruleId}`)
}

/**
 * Toggle a scoring rule on/off
 */
export async function toggleScoringRule(ruleId: string): Promise<LeadScoringRule> {
  const response = await apiClient.post<RuleResponse>(`/lead-scoring/rules/${ruleId}/toggle`)
  return response.data.data
}

// ============== Scores ==============

/**
 * Get lead scores
 */
export async function getLeadScores(params?: {
  grade?: string
  limit?: number
}): Promise<LeadScore[]> {
  const searchParams = new URLSearchParams()
  if (params?.grade) searchParams.set('grade', params.grade)
  if (params?.limit) searchParams.set('limit', String(params.limit))

  const queryString = searchParams.toString()
  const response = await apiClient.get<ScoresResponse>(
    `/lead-scoring/scores${queryString ? `?${queryString}` : ''}`
  )
  return response.data.data
}

/**
 * Get score for a contact
 */
export async function getContactScore(contactId: string): Promise<LeadScore> {
  const response = await apiClient.get<ScoreResponse>(`/lead-scoring/contacts/${contactId}/score`)
  return response.data.data
}

/**
 * Calculate/recalculate score for a contact
 */
export async function calculateContactScore(contactId: string): Promise<LeadScore> {
  const response = await apiClient.post<ScoreResponse>(
    `/lead-scoring/contacts/${contactId}/calculate`
  )
  return response.data.data
}

/**
 * Recalculate scores for all contacts
 */
export async function recalculateAllScores(): Promise<{ contacts_processed: number }> {
  const response = await apiClient.post<{ data: { contacts_processed: number } }>(
    '/lead-scoring/recalculate-all'
  )
  return response.data.data
}

// ============== Configuration ==============

/**
 * Get lead scoring configuration
 */
export async function getScoringConfig(): Promise<LeadScoringConfig> {
  const response = await apiClient.get<ConfigResponse>('/lead-scoring/config')
  return response.data.data
}

/**
 * Update lead scoring configuration
 */
export async function updateScoringConfig(
  data: Partial<LeadScoringConfig>
): Promise<void> {
  await apiClient.put('/lead-scoring/config', data)
}

// ============== Options ==============

/**
 * Get scoring options for creating rules
 */
export async function getScoringOptions(): Promise<ScoringOptions> {
  const response = await apiClient.get<OptionsResponse>('/lead-scoring/options')
  return response.data.data
}

// ============== Helpers ==============

/**
 * Get grade color
 */
export function getGradeColor(grade: string): string {
  switch (grade) {
    case 'A':
      return 'green'
    case 'B':
      return 'blue'
    case 'C':
      return 'yellow'
    case 'D':
      return 'orange'
    case 'F':
      return 'red'
    default:
      return 'gray'
  }
}

/**
 * Get grade badge variant
 */
export function getGradeBadgeVariant(grade: string): 'success' | 'info' | 'warning' | 'danger' | 'gray' {
  switch (grade) {
    case 'A':
      return 'success'
    case 'B':
      return 'info'
    case 'C':
      return 'warning'
    case 'D':
    case 'F':
      return 'danger'
    default:
      return 'gray'
  }
}

/**
 * Format score for display
 */
export function formatScore(score: number): string {
  return score.toString()
}
